import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ApiService } from './api.service';
import { PageActionService } from './page-action.service';
import { McpContextService } from './mcp-context.service';
import { McpTraceService } from './mcp-trace.service';
import { McpIntentRouterService } from './mcp-intent-router.service';

export type McpRealtimeStatus = 'idle' | 'connecting' | 'active' | 'error';

export interface McpTranscriptEntry {
    role: 'user' | 'assistant' | 'system';
    text: string;
    at: string;
}

@Injectable({ providedIn: 'root' })
export class McpRealtimeService {
    private _pc: RTCPeerConnection | null = null;
    private _dc: RTCDataChannel | null = null;
    private _localStream: MediaStream | null = null;
    private _audioEl: HTMLAudioElement | null = null;
    private _sessionId: string | null = null;
    private _contextSub: Subscription | null = null;
    private _micSender: RTCRtpSender | null = null;

    private _activeResponses = 0;
    private _pendingResponseCreate = false;
    private _responseCreateTimer: any = null;
    private _responseWatchdogTimer: any = null;
    private _lastAudioTranscriptAt = 0;
    private _lastAudioLikelyNoise = false;
    private _lastAudioTranscript = '';
    private _chirpPlayedAt = 0;
    private readonly _postChirpGuardMs = 120;
    private readonly _wakeWordRegex = /^computer\b[!. ]*$/i;
    private readonly _endSessionRegex = /^(that'?s all for now|thats all for now|that'?s it|thats it|all done for now)\b[!. ]*$/i;
    private readonly _sessionReadyCueUrl = 'https://www.trekcore.com/audio/communicator/tng_chirp_clean.mp3';
    private _sessionReadyCueEl: HTMLAudioElement | null = null;
    private _wakeRecognition: any = null;
    private _wakeListening = false;
    private _contextEpoch = 1;
    private _pendingBackendResults: Array<{
        command: string;
        result: any;
        requestId: string;
    }> = [];

    readonly status$ = new BehaviorSubject<McpRealtimeStatus>('idle');
    readonly transcript$ = new BehaviorSubject<McpTranscriptEntry[]>([]);
    readonly aiSpeaking$ = new BehaviorSubject<boolean>(false);
    readonly micMuted$ = new BehaviorSubject<boolean>(true);

    constructor(
        private readonly zone: NgZone,
        private readonly api: ApiService,
        private readonly pageActionService: PageActionService,
        private readonly contextService: McpContextService,
        private readonly traceService: McpTraceService,
        private readonly intentRouter: McpIntentRouterService
    ) {}

    async start(): Promise<void> {
        if (this.status$.value === 'connecting' || this.status$.value === 'active') {
            return;
        }

        const snapshot = this.contextService.getSnapshot();
        if (!this.canStartOnCurrentPage(snapshot)) {
            this.status$.next('idle');
            this.addTranscript(
                'system',
                'AI assistant is available on mapped pages only (Receiving, Shipping, Printer View).'
            );
            this.traceService.log(
                'realtime_start_skipped_unmapped_page',
                { pageLabel: snapshot.pageLabel, route: snapshot.route, actionCount: snapshot.actions?.length ?? 0 },
                { detail: 'Skipped start on unmapped page' }
            );
            return;
        }

        this.status$.next('connecting');
        this.traceService.log('realtime_start_requested', {}, { detail: 'Starting Realtime connection' });

        try {
            const promptContext = this.contextService.buildPromptContext(snapshot);
            console.log('[MCP Realtime] Sending context to session:', promptContext);

            let sessionRes: any;
            try {
                sessionRes = await this.api
                    .postApi('mcp/voice/session', {
                        context: {
                            prompt_context: promptContext,
                            user_name: snapshot.userName,
                            user_role: snapshot.role,
                            anonymous: snapshot.anonymous,
                        },
                    })
                    .toPromise();
            } catch (httpErr: any) {
                const status = httpErr?.status || 'unknown';
                const body = httpErr?.error || httpErr?.message || 'Unknown backend error';
                console.error(`McpRealtimeService: backend session failed (HTTP ${status}):`, body);
                throw new Error(`Backend session failed (HTTP ${status}): ${JSON.stringify(body)}`);
            }

            if (!sessionRes?.client_secret) {
                console.error('McpRealtimeService: No client_secret in response:', sessionRes);
                throw new Error('No client_secret returned from session endpoint');
            }

            this._localStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            this._sessionId = sessionRes.session_id;
            const ephemeralKey = sessionRes.client_secret;

            this._pc = new RTCPeerConnection();

            this._audioEl = document.createElement('audio');
            this._audioEl.autoplay = true;
            this._pc.ontrack = (ev) => {
                this._audioEl!.srcObject = ev.streams[0];
            };

            this._localStream.getTracks().forEach((track) => {
                track.enabled = false;
                this._micSender = this._pc!.addTrack(track, this._localStream!);
            });
            this.micMuted$.next(true);

            this._dc = this._pc.createDataChannel('oai-events');
            this._dc.onmessage = (ev) => this.handleDataChannelMessage(ev);

            const offer = await this._pc.createOffer();
            await this._pc.setLocalDescription(offer);

            const sdpResponse = await fetch(
                'https://api.openai.com/v1/realtime?model=gpt-4o-realtime-preview-2025-06-03',
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${ephemeralKey}`,
                        'Content-Type': 'application/sdp',
                    },
                    body: offer.sdp,
                }
            );

            if (!sdpResponse.ok) {
                throw new Error(`OpenAI Realtime SDP failed: ${sdpResponse.status}`);
            }

            const answerSdp = await sdpResponse.text();
            await this._pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

            this._contextSub = this.contextService.contextUpdate$.subscribe((update) => {
                this.sendSessionUpdate(update);
            });

            this.status$.next('active');
            this.prepareSessionReadyCue();
            this.syncWakeWordListener();
            this.traceService.log('realtime_connected', { sessionId: this._sessionId }, { detail: 'WebRTC connected' });
        } catch (err: any) {
            console.error('McpRealtimeService start failed:', err);
            this.status$.next('error');
            this.traceService.log('realtime_start_failed', { error: err?.message }, { detail: 'Failed to start Realtime' });
            this.cleanup();
        }
    }

    async prewarm(): Promise<void> {
        const status = this.status$.value;
        if (status === 'active' || status === 'connecting') {
            return;
        }
        await this.start();
        // Keep muted after prewarm; user can unmute when ready.
        this.setMicMuted(true);
        this.traceService.log('realtime_prewarmed', {}, { detail: 'Realtime session prewarmed in background' });
    }

    stop(): void {
        this.traceService.log('realtime_stop', {}, { detail: 'User requested stop' });
        this.cleanup();
    }

    setMicMuted(muted: boolean): void {
        if (this._localStream) {
            // Hard mute for Realtime stream. Wake word remains local-only.
            this._localStream.getAudioTracks().forEach((t) => (t.enabled = !muted));
        }
        this.micMuted$.next(muted);
        this.syncWakeWordListener();
    }

    toggleMic(): void {
        this.setMicMuted(!this.micMuted$.value);
    }

    cleanup(): void {
        this._contextSub?.unsubscribe();
        this._contextSub = null;

        if (this._dc) {
            try { this._dc.close(); } catch {}
            this._dc = null;
        }
        if (this._pc) {
            try { this._pc.close(); } catch {}
            this._pc = null;
        }
        if (this._localStream) {
            this._localStream.getTracks().forEach((t) => t.stop());
            this._localStream = null;
        }
        if (this._audioEl) {
            this._audioEl.srcObject = null;
            this._audioEl = null;
        }
        if (this._sessionReadyCueEl) {
            try {
                this._sessionReadyCueEl.pause();
                this._sessionReadyCueEl.src = '';
            } catch {}
            this._sessionReadyCueEl = null;
        }
        this.stopWakeWordListener();
        this._wakeRecognition = null;

        this._activeResponses = 0;
        this._pendingResponseCreate = false;
        if (this._responseCreateTimer) {
            clearTimeout(this._responseCreateTimer);
            this._responseCreateTimer = null;
        }
        if (this._responseWatchdogTimer) {
            clearTimeout(this._responseWatchdogTimer);
            this._responseWatchdogTimer = null;
        }
        this._pendingBackendResults = [];
        this._sessionId = null;
        this._micSender = null;
        this.aiSpeaking$.next(false);
        this.micMuted$.next(true);

        if (this.status$.value !== 'idle') {
            this.status$.next('idle');
        }
    }

    sendText(text: string): void {
        if (this.status$.value !== 'active' || !this._dc || this._dc.readyState !== 'open') {
            return;
        }

        this.interruptInFlightResponse('user_text');
        this.addTranscript('user', text);
        this.traceService.log('user_text_input', { text }, { detail: 'Text sent to Agent 1' });

        this._dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
                type: 'message',
                role: 'user',
                content: [{ type: 'input_text', text }],
            },
        }));

        this.requestAssistantResponse('user_text');
    }

    private handleDataChannelMessage(ev: MessageEvent): void {
        this.zone.run(() => {
            let msg: any;
            try {
                msg = JSON.parse(ev.data);
            } catch {
                return;
            }

            switch (msg.type) {
                case 'response.created':
                    if (this.isWithinPostChirpGuard()) {
                        this.traceService.log(
                            'response_suppressed_post_chirp',
                            { guardMs: this._postChirpGuardMs },
                            { detail: 'Suppressed response.created during post-chirp guard window' }
                        );
                        this.interruptInFlightResponse('user_text');
                        break;
                    }
                    this._activeResponses++;
                    this.aiSpeaking$.next(true);
                    this.armResponseWatchdog();
                    break;

                case 'response.done':
                    this.onResponseTerminalEvent();
                    break;

                // Realtime can terminate a response without always emitting response.done.
                // Treat all terminal variants the same so active-response tracking never wedges.
                case 'response.cancelled':
                case 'response.failed':
                case 'response.incomplete':
                    this.onResponseTerminalEvent();
                    break;

                case 'response.audio_transcript.done':
                    if (msg.transcript) {
                        this.addTranscript('assistant', msg.transcript);
                    }
                    break;

                case 'response.text.done':
                    if (msg.text) {
                        this.addTranscript('assistant', msg.text);
                    }
                    break;

                case 'conversation.item.input_audio_transcription.completed':
                    if (msg.transcript) {
                        const transcript = String(msg.transcript).trim();
                        if (this.micMuted$.value) {
                            this.traceService.log(
                                'muted_audio_ignored',
                                { transcript },
                                { detail: 'Ignored audio transcript while muted' }
                            );
                            return;
                        }

                        if (this.isEndSessionPhrase(transcript)) {
                            this.playSessionReadyCue();
                            this.setMicMuted(true);
                            this.interruptInFlightResponse('user_text');
                            this.addTranscript('system', 'Mic muted. Say "computer" to wake.');
                            this.traceService.log(
                                'voice_session_muted_by_phrase',
                                { transcript },
                                { detail: 'Muted mic from user end-session phrase' }
                            );
                            return;
                        }
                        const likelyNoise = this.isLikelyNoiseTranscript(transcript);
                        this._lastAudioTranscriptAt = Date.now();
                        this._lastAudioLikelyNoise = likelyNoise;
                        this._lastAudioTranscript = transcript;

                        if (likelyNoise) {
                            this.traceService.log(
                                'audio_noise_ignored',
                                { transcript },
                                { detail: 'Ignored likely background-noise transcript' }
                            );
                            return;
                        }
                        this.addTranscript('user', transcript);
                    }
                    break;

                case 'response.function_call_arguments.done':
                    this.handleToolCall(msg);
                    break;

                case 'error':
                    {
                        const code = msg?.error?.code;
                        // Benign race: we requested response.cancel, but the response
                        // already completed server-side before cancel arrived.
                        if (code === 'response_cancel_not_active') {
                            this.traceService.log(
                                'realtime_error_ignored',
                                { error: msg.error },
                                { detail: 'Ignored benign cancel-not-active race' }
                            );
                        } else {
                            console.error('Realtime error:', msg.error);
                            this.traceService.log('realtime_error', { error: msg.error }, { detail: 'Data channel error' });
                        }
                    }
                    break;
            }
        });
    }

    private handleToolCall(msg: any): void {
        const fnName = msg.name;
        const callId = msg.call_id;
        let args: any = {};
        try {
            args = JSON.parse(msg.arguments || '{}');
        } catch {}

        if (this.shouldBlockToolCallForNoise()) {
            this.traceService.log(
                'tool_call_blocked_noise',
                { function: fnName, args, transcript: this._lastAudioTranscript },
                { detail: 'Blocked tool call likely caused by background noise' }
            );
            this.sendToolResult(
                callId,
                JSON.stringify({
                    success: false,
                    message: 'Ignored likely background noise. Please repeat that command clearly.',
                })
            );
            return;
        }

        this.traceService.log('tool_call_received', { function: fnName, args, callId }, { detail: `Agent 1 called ${fnName}` });
        this.interruptInFlightResponse('tool_call');

        if (fnName === 'page_action') {
            this.handlePageAction(callId, args);
        } else if (fnName === 'backend_request') {
            this.handleBackendRequest(callId, args);
        } else {
            // Model called an action name directly instead of wrapping in page_action
            this.handlePageAction(callId, { action: fnName, params: args });
        }
    }

    private async handlePageAction(callId: string, args: any): Promise<void> {
        let action: string = args.action ?? '';

        // LLM sometimes sends params under "params" or "parameters" — normalize
        let params: Record<string, any> =
            (args.params && typeof args.params === 'object' && Object.keys(args.params).length > 0)
                ? { ...args.params }
            : (args.parameters && typeof args.parameters === 'object' && Object.keys(args.parameters).length > 0)
                ? { ...args.parameters }
            : {};

        // If params is still empty, collect extra keys from args root (LLM sometimes flattens)
        if (Object.keys(params).length === 0) {
            const skip = new Set(['action', 'params', 'parameters']);
            for (const [k, v] of Object.entries(args)) {
                if (!skip.has(k) && v !== undefined && v !== null) {
                    params[k] = v;
                }
            }
        }

        // LLM sometimes nests actual params under wrappers like actionParams/arguments/input.
        params = this.flattenActionParamWrappers(params);

        // LLM sometimes joins action:value (e.g. "set_filter:Processing") — split and remap
        if (action.includes(':') && !action.includes('(')) {
            const [base, value] = action.split(':', 2);
            action = base.trim();
            if (value && Object.keys(params).length === 0) {
                params = { value: value.trim() };
            }
        }

        // LLM sometimes copies param signature into action name, e.g.
        // "open_receiving_order(order_id: '495', tracking_number: '')"
        // Extract the real action name and parse inline params
        if (action.includes('(')) {
            const parenIdx = action.indexOf('(');
            const rawInner = action.slice(parenIdx + 1).replace(/\)$/, '');
            action = action.slice(0, parenIdx).trim();

            if (Object.keys(params).length === 0 && rawInner.trim()) {
                for (const part of rawInner.split(',')) {
                    const colonIdx = part.indexOf(':');
                    if (colonIdx > 0) {
                        const key = part.slice(0, colonIdx).trim().replace(/['"]/g, '');
                        const val = part.slice(colonIdx + 1).trim().replace(/^['"]|['"]$/g, '');
                        if (key && val) {
                            params[key] = val;
                        }
                    }
                }
            }
        }

        console.log('[MCP handlePageAction] action:', action, 'params:', JSON.stringify(params), 'raw args:', JSON.stringify(args));

        const beforeDispatchSnapshot = this.contextService.getSnapshot();
        const resolved = this.intentRouter.resolvePageAction(beforeDispatchSnapshot, action, params);
        const effectiveAction = String(resolved.action ?? action).trim();
        this.traceService.log(
            'page_action_call',
            {
                action,
                effectiveAction,
                params,
                raw_args: args,
                corrected: resolved.corrected,
                confidence: resolved.confidence,
                unresolved: resolved.unresolved,
            },
            { detail: `page_action("${action}" -> "${effectiveAction}", ${JSON.stringify(params)})` }
        );

        const dispatched = await this.pageActionService.dispatch(effectiveAction, params);

        this.contextService.logAction(
            'agent1_page_action',
            `${effectiveAction}${params ? '(' + JSON.stringify(params) + ')' : ''}`,
            dispatched ? 'dispatched' : 'not_found'
        );

        this.traceService.log(
            'page_action_dispatched',
            { action, effectiveAction, params, dispatched },
            { detail: dispatched ? 'Action dispatched successfully' : 'Action not found' }
        );

        const boundaryAction = dispatched ? this.getBoundaryAction(effectiveAction, params) : null;
        if (boundaryAction) {
            this._contextEpoch++;
            this.traceService.log(
                'context_epoch_advanced',
                { contextEpoch: this._contextEpoch, boundaryAction },
                { detail: `Context epoch advanced on ${boundaryAction}` }
            );
        }

        const snapshot = this.contextService.getSnapshot();
        const resultPayload = this.buildPageActionResult(
            effectiveAction,
            params,
            dispatched,
            snapshot,
            boundaryAction
        );

        this.sendToolResult(callId, JSON.stringify(resultPayload));

        // Fail-safe UX protocol: always acknowledge failed actions to the user,
        // even if the model does not produce a follow-up utterance.
        if (!dispatched) {
            const fallback = this.buildActionFailureFallbackMessage(action, params, snapshot, resultPayload?.message);
            this.addTranscript('assistant', fallback);
            this.traceService.log(
                'page_action_failure_fallback_sent',
                { action, params },
                { detail: 'Sent explicit fallback assistant message for failed action' }
            );
        }

        this.traceService.log(
            'page_state_in_tool_result',
            { dataSummary: snapshot.dataSummary, actionCount: snapshot.actions.length },
            { detail: 'State included in tool result payload' }
        );
    }

    private flattenActionParamWrappers(input: Record<string, any>): Record<string, any> {
        const wrappers = ['actionParams', 'params', 'parameters', 'arguments', 'input'];
        let current: Record<string, any> = { ...(input || {}) };

        // Unwrap up to a few levels to avoid pathological nesting.
        for (let i = 0; i < 4; i++) {
            const keys = Object.keys(current);
            if (keys.length === 1) {
                const key = keys[0];
                const value = current[key];
                if (
                    wrappers.includes(key) &&
                    value &&
                    typeof value === 'object' &&
                    !Array.isArray(value)
                ) {
                    current = { ...value };
                    continue;
                }
            }
            break;
        }

        // If wrapper key exists alongside others, merge its object contents to top-level.
        wrappers.forEach((key) => {
            const value = current[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                current = { ...value, ...current };
                delete current[key];
            }
        });

        return current;
    }

    private handleBackendRequest(callId: string, args: any): void {
        const command = args.command || '';

        this.traceService.log(
            'backend_request_received',
            { command },
            { detail: 'Backend request received — returning stub' }
        );

        this.contextService.logAction('agent1_backend_request', command, 'stub_ack');

        const snapshot = this.contextService.getSnapshot();
        const recentLog = snapshot.actionLog
            .slice(-5)
            .map(l => `${l.detail}${l.result ? ' → ' + l.result : ''}`)
            .join('; ');

        this.sendToolResult(
            callId,
            JSON.stringify({
                status: 'unavailable',
                message: 'I cannot look up data yet — backend requests are not connected. But based on what we did this session, here is the recent action log: ' + recentLog,
            })
        );
    }

    private sendToolResult(callId: string, output: string): void {
        if (!this._dc || this._dc.readyState !== 'open') {
            return;
        }

        this._dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
                type: 'function_call_output',
                call_id: callId,
                output,
            },
        }));

        this.requestAssistantResponse('tool_result');
    }

    private sendSessionUpdate(contextText: string): void {
        // Don't use session.update for context — it replaces the entire system prompt
        // and wipes out identity, behavioral rules, and workflow instructions.
        // Context updates are already included in tool results via dataSummary.
    }

    private interruptInFlightResponse(source: 'user_text' | 'tool_call'): void {
        if (!this._dc || this._dc.readyState !== 'open') {
            return;
        }
        if (this._activeResponses <= 0) {
            return;
        }

        try {
            this._dc.send(JSON.stringify({ type: 'response.cancel' }));
        } catch {}

        this.traceService.log(
            'response_interrupted',
            { source, activeResponses: this._activeResponses },
            { detail: 'Cancelled in-flight response for newer user input' }
        );

        this._activeResponses = 0;
        this.aiSpeaking$.next(false);
        this.drainBackendResults();
        if (this._pendingResponseCreate) {
            this._pendingResponseCreate = false;
            this.requestAssistantResponse('after_interrupt');
        }
    }

    private onResponseTerminalEvent(): void {
        this._activeResponses = Math.max(0, this._activeResponses - 1);
        if (this._activeResponses === 0) {
            if (this._responseWatchdogTimer) {
                clearTimeout(this._responseWatchdogTimer);
                this._responseWatchdogTimer = null;
            }
            this.aiSpeaking$.next(false);
            this.drainBackendResults();
            if (this._pendingResponseCreate) {
                this._pendingResponseCreate = false;
                this.requestAssistantResponse('queued_after_terminal');
            }
        }
    }

    private requestAssistantResponse(reason: string): void {
        if (!this._dc || this._dc.readyState !== 'open') {
            return;
        }

        if (this._activeResponses > 0) {
            this._pendingResponseCreate = true;
            this.traceService.log(
                'response_create_queued',
                { reason, activeResponses: this._activeResponses },
                { detail: 'Deferred response.create until idle' }
            );
            return;
        }

        if (this._responseCreateTimer) {
            clearTimeout(this._responseCreateTimer);
        }

        this._responseCreateTimer = setTimeout(() => {
            if (!this._dc || this._dc.readyState !== 'open') {
                return;
            }
            this._dc.send(JSON.stringify({ type: 'response.create' }));
            this.traceService.log(
                'response_create_sent',
                { reason },
                { detail: 'Issued response.create' }
            );
            this._responseCreateTimer = null;
        }, 60);
    }

    private armResponseWatchdog(): void {
        if (this._responseWatchdogTimer) {
            clearTimeout(this._responseWatchdogTimer);
        }

        this._responseWatchdogTimer = setTimeout(() => {
            if (this._activeResponses <= 0) {
                return;
            }

            this.traceService.log(
                'response_watchdog_reset',
                { activeResponses: this._activeResponses },
                { detail: 'Watchdog forced response recovery after timeout' }
            );

            this._activeResponses = 0;
            this.aiSpeaking$.next(false);
            this.drainBackendResults();
            if (this._pendingResponseCreate) {
                this._pendingResponseCreate = false;
                this.requestAssistantResponse('watchdog_recovery');
            }
        }, 15000);
    }

    private drainBackendResults(): void {
        if (this._pendingBackendResults.length === 0 || this._activeResponses > 0) {
            return;
        }

        const items = [...this._pendingBackendResults];
        this._pendingBackendResults = [];

        const combined = items
            .map((item) => `[BACKEND RESULT for "${item.command}"]\n${JSON.stringify(item.result)}`)
            .join('\n\n');

        if (!this._dc || this._dc.readyState !== 'open') {
            return;
        }

        this._dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
                type: 'message',
                role: 'user',
                content: [{ type: 'input_text', text: combined }],
            },
        }));

        this.requestAssistantResponse('backend_results');

        this.traceService.log(
            'backend_results_injected',
            { count: items.length, requestIds: items.map((i) => i.requestId) },
            { detail: 'Queued backend results drained into conversation' }
        );
    }

    private addTranscript(role: 'user' | 'assistant' | 'system', text: string): void {
        const entry: McpTranscriptEntry = {
            role,
            text,
            at: new Date().toISOString(),
        };
        const next = [...this.transcript$.value, entry].slice(-40);
        this.transcript$.next(next);
    }

    private buildPageActionResult(
        action: string,
        params: Record<string, any>,
        success: boolean,
        snapshot: any,
        boundaryAction: string | null = null
    ): any {
        const orderId = String(params?.['order_id'] ?? '').trim();
        const tracking = String(params?.['tracking_number'] ?? '').trim();

        let message = success
            ? `Action "${action}" executed.`
            : `Action "${action}" failed. Check action name and params.`;

        if (action === 'open_receiving_order') {
            if (success) {
                if (orderId) {
                    message = `Opened receiving order ${orderId}.`;
                } else if (tracking) {
                    message = `Opened receiving order for tracking number ${tracking}.`;
                } else {
                    message = 'Opened receiving order.';
                }
            } else {
                if (orderId) {
                    message = `Order ${orderId} was not found in loaded receiving orders.`;
                } else if (tracking) {
                    message = `Tracking number ${tracking} was not found in loaded receiving orders.`;
                } else {
                    message = 'No matching receiving order was found in loaded orders.';
                }
            }
        }
        if (action === 'open_shipping_order') {
            if (success) {
                if (orderId) {
                    message = `Opened shipping order ${orderId}.`;
                } else if (tracking) {
                    message = `Opened shipping order for tracking number ${tracking}.`;
                } else {
                    message = 'Opened shipping order.';
                }
            } else {
                if (orderId) {
                    message = `Order ${orderId} was not found in loaded shipping orders.`;
                } else if (tracking) {
                    message = `Tracking number ${tracking} was not found in loaded shipping orders.`;
                } else {
                    message = 'No matching shipping order was found in loaded orders.';
                }
            }
        }

        return {
            success,
            message,
            state: this.buildLayeredStatePayload(action, snapshot, boundaryAction),
        };
    }

    private buildActionFailureFallbackMessage(
        action: string,
        params: Record<string, any>,
        snapshot: any,
        detailMessage?: string
    ): string {
        const candidateActions = Array.isArray(snapshot?.actions)
            ? snapshot.actions.map((a: any) => String(a?.action ?? '').trim()).filter(Boolean)
            : [];
        const hintActions = candidateActions.slice(0, 6).join(', ');

        const id = String(params?.['order_id'] ?? '').trim();
        const detail = String(detailMessage ?? '').trim();
        const subject = id ? `order ${id}` : 'that request';

        if (action === 'close_order_drawer' || action === 'close_receiving_order' || action === 'close_shipping_order') {
            return `I could not close the drawer for ${subject}. Try "close drawer".`;
        }

        if (detail) {
            return `I could not do that. ${detail}${hintActions ? ` Available actions here include: ${hintActions}.` : ''}`;
        }

        return `I could not do that action ("${action}").${hintActions ? ` Available actions here include: ${hintActions}.` : ''}`;
    }

    private buildLayeredStatePayload(
        action: string,
        snapshot: any,
        boundaryAction: string | null
    ): Record<string, any> {
        const dataSummary = String(snapshot?.dataSummary ?? '');
        const overview = this.buildOverviewSummary(dataSummary);
        const payload: Record<string, any> = {
            page: snapshot.pageLabel,
            route: snapshot.route,
            dataSummary,
            awareness: snapshot.awareness,
            focus: snapshot.focus ?? null,
            contextEpoch: this._contextEpoch,
            layers: { overview },
        };
        if (boundaryAction) {
            payload.resetScope = 'task_boundary';
            payload.boundaryAction = boundaryAction;
        }

        // Include richer detail only on merchandise-related turns to keep normal turns lean.
        if (
            action === 'describe_open_merchandise' ||
            action === 'open_receiving_order' ||
            /merchandise|sizes?|counts?/i.test(dataSummary)
        ) {
            const focused = this.extractMerchandiseFocusedDetail(dataSummary);
            if (focused) {
                payload.layers.focused = focused;
            }
        }

        return payload;
    }

    private getBoundaryAction(action: string, params: Record<string, any>): string | null {
        const normalized = action.trim();
        if (normalized === 'close_drawer') return normalized;
        if (normalized === 'clear_filters') return normalized;
        if (normalized === 'refresh') return normalized;
        if (normalized === 'next_page' || normalized === 'previous_page') return normalized;

        if (normalized === 'open_receiving_order' || normalized === 'open_shipping_order') {
            const id = String(params?.['order_id'] ?? '').trim();
            return id ? `${normalized}:${id}` : normalized;
        }

        return null;
    }

    private buildOverviewSummary(dataSummary: string): string {
        const firstSentence = dataSummary
            .split('.')
            .map((s) => s.trim())
            .find(Boolean) ?? '';
        return firstSentence ? `${firstSentence}.` : dataSummary;
    }

    private extractMerchandiseFocusedDetail(dataSummary: string): string | null {
        const marker = 'Merchandise check:';
        const idx = dataSummary.indexOf(marker);
        if (idx < 0) {
            return null;
        }

        const text = dataSummary.slice(idx).trim();
        if (!text) {
            return null;
        }

        return text.length > 900 ? `${text.slice(0, 900)}...` : text;
    }

    private shouldBlockToolCallForNoise(): boolean {
        const ageMs = Date.now() - this._lastAudioTranscriptAt;
        return this._lastAudioLikelyNoise && ageMs >= 0 && ageMs <= 5000;
    }

    private isLikelyNoiseTranscript(text: string): boolean {
        const t = text.trim();
        if (!t) return true;
        if (t.length < 2) return true;

        const symbolCount = (t.match(/[^A-Za-z0-9\s.,?!'":;\-]/g) || []).length;
        const symbolRatio = symbolCount / Math.max(1, t.length);
        if (symbolRatio > 0.30) return true;

        const asciiPrintable = (t.match(/[\x20-\x7E]/g) || []).length / Math.max(1, t.length);
        if (asciiPrintable < 0.60) return true;

        const alphaWords = t
            .split(/\s+/)
            .filter(Boolean)
            .filter((w) => /[A-Za-z]{2,}/.test(w)).length;
        if (alphaWords === 0) return true;

        const collapsed = t.replace(/\s+/g, '');
        if (/^(.)\1{4,}$/.test(collapsed)) return true;

        return false;
    }

    private isWakeWordTranscript(text: string): boolean {
        return this._wakeWordRegex.test(String(text ?? '').trim());
    }

    private isEndSessionPhrase(text: string): boolean {
        return this._endSessionRegex.test(String(text ?? '').trim().toLowerCase());
    }

    private playSessionReadyCue(): void {
        try {
            this.prepareSessionReadyCue();
            if (!this._sessionReadyCueEl) return;
            this._chirpPlayedAt = Date.now();
            this._sessionReadyCueEl.currentTime = 0;
            const playPromise = this._sessionReadyCueEl.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise.catch((err) => {
                    this.traceService.log(
                        'session_ready_cue_blocked',
                        { error: err?.message ?? String(err) },
                        { detail: 'Session ready sound blocked by browser autoplay policy' }
                    );
                });
            }
        } catch (err: any) {
            this.traceService.log(
                'session_ready_cue_failed',
                { error: err?.message ?? String(err) },
                { detail: 'Failed to play session ready sound' }
            );
        }
    }

    private prepareSessionReadyCue(): void {
        if (this._sessionReadyCueEl) {
            return;
        }
        try {
            this._sessionReadyCueEl = new Audio(this._sessionReadyCueUrl);
            this._sessionReadyCueEl.preload = 'auto';
            this._sessionReadyCueEl.volume = 1.0;
            this._sessionReadyCueEl.load();
        } catch (err: any) {
            this.traceService.log(
                'session_ready_cue_prepare_failed',
                { error: err?.message ?? String(err) },
                { detail: 'Failed to prewarm session ready cue audio' }
            );
            this._sessionReadyCueEl = null;
        }
    }

    private syncWakeWordListener(): void {
        if (this.status$.value === 'active' && this.micMuted$.value) {
            this.startWakeWordListener();
        } else {
            this.stopWakeWordListener();
        }
    }

    private startWakeWordListener(): void {
        if (this._wakeListening) {
            return;
        }
        const win = window as any;
        const SpeechRecognitionCtor =
            win.SpeechRecognition || win.webkitSpeechRecognition;
        if (!SpeechRecognitionCtor) {
            this.traceService.log(
                'wake_word_not_supported',
                {},
                { detail: 'SpeechRecognition API unavailable; wake word disabled' }
            );
            return;
        }

        if (!this._wakeRecognition) {
            this._wakeRecognition = new SpeechRecognitionCtor();
            this._wakeRecognition.continuous = true;
            this._wakeRecognition.interimResults = false;
            this._wakeRecognition.lang = 'en-US';

            this._wakeRecognition.onresult = (event: any) => {
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    if (!result?.isFinal) {
                        continue;
                    }
                    const transcript = String(result[0]?.transcript ?? '').trim();
                    if (!this.isWakeWordTranscript(transcript)) {
                        continue;
                    }
                    if (!this.micMuted$.value) {
                        return;
                    }
                    this.setMicMuted(false);
                    this.playSessionReadyCue();
                    this.traceService.log(
                        'wake_word_local_detected',
                        { transcript },
                        { detail: 'Local wake word detected; mic unmuted and cue played' }
                    );
                    return;
                }
            };

            this._wakeRecognition.onerror = (event: any) => {
                this.traceService.log(
                    'wake_word_listener_error',
                    { error: event?.error ?? 'unknown' },
                    { detail: 'Wake word listener error' }
                );
            };

            this._wakeRecognition.onend = () => {
                this._wakeListening = false;
                if (this.status$.value === 'active' && this.micMuted$.value) {
                    setTimeout(() => this.startWakeWordListener(), 250);
                }
            };
        }

        try {
            this._wakeRecognition.start();
            this._wakeListening = true;
            this.traceService.log(
                'wake_word_listener_started',
                {},
                { detail: 'Local wake word listener started' }
            );
        } catch (err: any) {
            this.traceService.log(
                'wake_word_listener_start_failed',
                { error: err?.message ?? String(err) },
                { detail: 'Failed to start local wake listener' }
            );
        }
    }

    private stopWakeWordListener(): void {
        if (!this._wakeRecognition || !this._wakeListening) {
            return;
        }
        try {
            this._wakeRecognition.stop();
        } catch {}
        this._wakeListening = false;
        this.traceService.log(
            'wake_word_listener_stopped',
            {},
            { detail: 'Local wake word listener stopped' }
        );
    }

    private isWithinPostChirpGuard(): boolean {
        if (!this._chirpPlayedAt) {
            return false;
        }
        return Date.now() - this._chirpPlayedAt <= this._postChirpGuardMs;
    }

    private canStartOnCurrentPage(snapshot: any): boolean {
        const pageLabel = String(snapshot?.pageLabel ?? '').trim().toLowerCase();
        const route = String(snapshot?.route ?? '').toLowerCase();
        const actionCount = Array.isArray(snapshot?.actions) ? snapshot.actions.length : 0;
        if (actionCount > 0) {
            return true;
        }
        if (pageLabel.includes('receiving') || pageLabel.includes('shipping') || pageLabel.includes('printer') || pageLabel.includes('mailbox') || pageLabel.includes('email')) {
            return true;
        }
        if (route.includes('/receiving') || route.includes('/shipping') || route.includes('/printer-view') || route.includes('/mailbox')) {
            return true;
        }
        return false;
    }

}
